/**
 * DealDetailPage — single-deal view. Stage selector (calls move-stage
 * verb), contact/company links, value/close-date/notes edit-in-place,
 * activity timeline, win-likelihood AI button + risk-level badge.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Sparkles, Trash2 } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsDeal,
  useFiasLeadsPipelines,
  useUpdateFiasLeadsDeal,
  useMoveFiasLeadsDealStage,
  useDeleteFiasLeadsDeal,
  useFiasLeadsCompanies,
} from '../hooks/useFiasLeads';
import { api } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { SkeletonBlock } from '../components/Skeletons';
import { MoneyInput } from '../components/MoneyInput';
import { DealContacts } from '../components/DealContacts';
import { SendDealEmailPanel } from '../components/SendDealEmailPanel';
import { ActivitiesTimelinePanel } from '../components/ActivitiesTimelinePanel';
import { formatMoneyValue } from '../format';

interface Props {
  holderId: string;
}

export function DealDetailPage({ holderId }: Props) {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const dealQuery = useFiasLeadsDeal(holderId, dealId ?? null);
  const pipelinesQuery = useFiasLeadsPipelines(holderId);
  const companiesQuery = useFiasLeadsCompanies(holderId);
  const updateDeal = useUpdateFiasLeadsDeal(holderId);
  const moveStage = useMoveFiasLeadsDealStage(holderId);
  const deleteDeal = useDeleteFiasLeadsDeal(holderId);

  const [edit, setEdit] = useState({
    value: 0,
    currency: 'USD',
    expectedCloseDate: '',
    notes: '',
    companyId: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (dealQuery.data) {
      setEdit({
        value: Number(dealQuery.data.value) || 0,
        currency: dealQuery.data.currency,
        expectedCloseDate: dealQuery.data.expected_close_date ?? '',
        notes: dealQuery.data.notes,
        companyId: dealQuery.data.company_id ?? '',
      });
      setIsDirty(false);
    }
  }, [dealQuery.data]);

  const handleSave = async () => {
    if (!dealId) return;
    await updateDeal.mutateAsync({
      dealId,
      input: {
        value: edit.value,
        currency: edit.currency,
        expectedCloseDate: edit.expectedCloseDate || null,
        notes: edit.notes,
        companyId: edit.companyId || null,
      },
    });
    setIsDirty(false);
  };

  const handleStageMove = async (stageId: string) => {
    if (!dealId) return;
    await moveStage.mutateAsync({ dealId, stageId });
  };

  const handleWinLikelihood = async () => {
    if (!dealId) return;
    setAiBusy(true);
    try {
      await api.fiasLeadsWinLikelihood(holderId, dealId);
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deal(holderId, dealId) });
    } catch (err) {
      // 501 expected until AI provider integration ships.
      alert((err as Error).message);
    } finally {
      setAiBusy(false);
    }
  };

  const handleRefreshRisk = async () => {
    if (!dealId) return;
    setAiBusy(true);
    try {
      await api.refreshFiasLeadsDealRisk(holderId, dealId);
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deal(holderId, dealId) });
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.dealRisk(holderId) });
    } finally {
      setAiBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!dealId) return;
    if (!confirm('Delete this deal? It is soft-deleted and can be restored.')) return;
    await deleteDeal.mutateAsync(dealId);
    navigate(`${ARCHE_PATH}/deals`);
  };

  if (dealQuery.isLoading) return <SkeletonBlock lines={8} />;
  if (dealQuery.isError || !dealQuery.data) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Link to={`${ARCHE_PATH}/deals`} className="text-sm text-muted-foreground hover:underline">
          ← Deals
        </Link>
        <p className="mt-4 text-destructive">Deal not found.</p>
      </div>
    );
  }

  const d = dealQuery.data;
  const pipeline = (pipelinesQuery.data ?? []).find((p) => p.id === d.pipeline_id);

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-6">
      <Link
        to={`${ARCHE_PATH}/deals`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline w-fit"
      >
        <ArrowLeft className="h-4 w-4" />
        Deals
      </Link>

      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <DealTitleEditor
            name={d.name}
            onRename={(name) => updateDeal.mutateAsync({ dealId: d.id, input: { name } })}
          />
          <p className="text-sm text-muted-foreground">
            {pipeline?.name ?? '?'} · {d.currency} {formatMoneyValue(d.value)}
            {d.risk_level && (
              <span
                className={
                  'ml-2 rounded px-2 py-0.5 text-xs ' +
                  (d.risk_level === 'high'
                    ? 'bg-rose-100 text-rose-700'
                    : d.risk_level === 'medium'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-emerald-100 text-emerald-700')
                }
              >
                Risk: {d.risk_level}
              </span>
            )}
            {d.win_likelihood != null && (
              <span className="ml-2 rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                Win {Number(d.win_likelihood).toFixed(0)}%
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleWinLikelihood}
            disabled={aiBusy}
            className="inline-flex items-center gap-2 text-sm rounded border border-border px-3 py-1.5 hover:bg-muted/30"
          >
            <Sparkles className="h-4 w-4" />
            {aiBusy ? 'Working…' : 'Win likelihood'}
          </button>
          <button
            onClick={handleRefreshRisk}
            disabled={aiBusy}
            className="text-sm rounded border border-border px-3 py-1.5 hover:bg-muted/30"
          >
            Refresh risk
          </button>
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </header>

      <div className="rounded border border-border bg-card p-4">
        <h2 className="text-sm font-medium text-muted-foreground mb-3">Stage</h2>
        {pipeline ? (
          <div className="flex flex-wrap gap-2">
            {pipeline.stages.map((s) => (
              <button
                key={s.id}
                onClick={() => handleStageMove(s.id)}
                disabled={moveStage.isPending || s.id === d.stage_id}
                className={
                  'rounded border px-3 py-1.5 text-sm ' +
                  (s.id === d.stage_id
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border hover:bg-muted/30 disabled:opacity-50')
                }
              >
                {s.name}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Pipeline unavailable.</p>
        )}
      </div>

      <div className="rounded border border-border bg-card p-4">
        <h2 className="text-sm font-medium text-muted-foreground mb-3">Details</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Value</span>
            <div className="flex gap-1">
              <MoneyInput
                value={edit.value}
                onChange={(next) => {
                  setEdit((p) => ({ ...p, value: next }));
                  setIsDirty(true);
                }}
                ariaLabel="Deal value"
                className="flex-1 rounded border border-border bg-background px-3 py-2"
              />
              <input
                type="text"
                value={edit.currency}
                onChange={(e) => {
                  setEdit((p) => ({ ...p, currency: e.target.value }));
                  setIsDirty(true);
                }}
                maxLength={3}
                className="w-16 rounded border border-border bg-background px-2 py-2 uppercase"
              />
            </div>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Expected close</span>
            <input
              type="date"
              value={edit.expectedCloseDate.slice(0, 10)}
              onChange={(e) => {
                setEdit((p) => ({ ...p, expectedCloseDate: e.target.value }));
                setIsDirty(true);
              }}
              className="rounded border border-border bg-background px-3 py-2"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Company</span>
            <select
              value={edit.companyId}
              onChange={(e) => {
                setEdit((p) => ({ ...p, companyId: e.target.value }));
                setIsDirty(true);
              }}
              className="rounded border border-border bg-background px-3 py-2"
            >
              <option value="">— None —</option>
              {(companiesQuery.data ?? []).map((co) => (
                <option key={co.companyId} value={co.companyId}>
                  {co.name}
                </option>
              ))}
            </select>
          </label>
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground">Notes</label>
            <textarea
              value={edit.notes}
              onChange={(e) => {
                setEdit((p) => ({ ...p, notes: e.target.value }));
                setIsDirty(true);
              }}
              rows={3}
              className="w-full rounded border border-border bg-background px-3 py-2"
            />
          </div>
        </div>
        {isDirty && (
          <button
            onClick={handleSave}
            disabled={updateDeal.isPending}
            className="mt-3 rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm"
          >
            {updateDeal.isPending ? 'Saving…' : 'Save changes'}
          </button>
        )}
        {d.risk_flags.length > 0 && (
          <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
            Risk flags: {d.risk_flags.join(', ')}
          </div>
        )}
      </div>

      <DealContacts holderId={holderId} dealId={d.id} />

      <SendDealEmailPanel holderId={holderId} dealId={d.id} dealName={d.name} />

      <ActivitiesTimelinePanel
        holderId={holderId}
        regardingType="fias_leads_deal"
        regardingId={d.id}
      />
    </div>
  );
}

/**
 * Inline-editable deal title — the rename affordance. Looks like the page
 * heading until hovered/focused, then reveals an editable field. Commits on
 * blur or Enter, reverts on Escape or an empty/unchanged value. Mirrors the
 * pipeline/stage rename pattern (PipelinesSettingsPage) so renaming feels the
 * same everywhere in the arche.
 */
function DealTitleEditor({
  name,
  onRename,
}: {
  name: string;
  onRename: (name: string) => Promise<unknown>;
}) {
  const [value, setValue] = useState(name);
  // Escape blurs the field to revert, but the blur-triggered commit runs with
  // the pre-revert closure value — this ref tells that commit to stand down.
  const skipCommitRef = useRef(false);

  // Re-seed when the server value changes (e.g. concurrent edit / refetch).
  useEffect(() => setValue(name), [name]);

  const commit = async () => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      setValue(name);
      return;
    }
    const trimmed = value.trim();
    if (!trimmed || trimmed === name) {
      setValue(name);
      return;
    }
    try {
      await onRename(trimmed);
    } catch (err) {
      setValue(name);
      alert(`Could not rename deal: ${(err as Error).message}`);
    }
  };

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          skipCommitRef.current = true;
          setValue(name);
          e.currentTarget.blur();
        }
      }}
      aria-label="Deal name"
      className="w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-2xl font-semibold hover:border-border focus:border-border focus:bg-background focus:outline-none"
    />
  );
}
